import type { ImageSearchResultType, ImageSearchResponseType } from '../types/type';
import getToken from '../../../utils/getToken';
import apiKey from '../../../utils/apiKey';
import { QUERY_KEYS } from '../constants/queryKeys';
import { queryGet } from '../../../utils/queryClient';
import { useMutation, useQueryClient } from 'react-query';
import type { MetaType, PhotoCardType } from '../../types/PhotoCardType';

/**
 * 이미지 검색 뮤테이션
 * 1. 이미지 검색 결과 요청
 * 2. 포카 아이디 추출, 포카아이디 정보 요청용 배열생성
 * 3. 포카 아이디 정보 요청
 * 4. 포카이미지검색결과와 포카 정보를 합쳐서 반환
 *
 * @return 포카이미지 검색 결과 + 포카정보 반환
 */
export const useSearchImageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ imageData, selectedMember, top_k = 6 }: ImageSearchResultType) => {
      //1. 이미지 검색 결과 요청
      const imageSearchResult = await getImageSearchResult(
        'https://cfr67z4tc7f3m77jt3wgrbgrna0pbfam.lambda-url.ap-northeast-2.on.aws/test/image-search',
        {
          artistId: selectedMember.artistId,
          memberId: selectedMember.memberId,
          dataURL: imageData,
          top_k: top_k
        }
      );

      if (!imageSearchResult.success) {
        //검색실패 시 바로 반환
        return imageSearchResult;
      }

      //2.포카 아이디 추출 및 중복제거, 포카아이디 정보 요청용 배열생성
      const photoCardIds = Array.from(
        new Set(
          imageSearchResult.results.flatMap((result) => {
            return result.candidates.map((candidate) => candidate.photoCardId);
          })
        )
      );

      if (photoCardIds.length === 0) {
        //포카 아이디가 없으면 바로 반환
        return imageSearchResult;
      }

     
      //3. 포카 아이디 정보 요청
      const photoCardInfo = await getPhotoCardInfo(
        selectedMember,
        Array.from(new Set(photoCardIds))
      );

      //4. 포카 아이디 정보와 유사도 정보 합치기
      const imageSearchResultWithPhotoCard = getImageSearchResultWithPhotoCard(
        imageSearchResult,
        photoCardInfo
      );
      return imageSearchResultWithPhotoCard;
    },
    onSuccess: (data) => {
      //포카이미지 검색 결과 + 포카정보 반환 캐시 저장
      queryClient.setQueryData(QUERY_KEYS.IMAGE_SEARCH_RESULT, data);
    }
  });
};

const getImageSearchResultWithPhotoCard = (
  imageSearchResult: ImageSearchResponseType,
  photoCardInfo: PhotoCardType[]
) => {
  const photoCardMap = new Map(photoCardInfo.map((pc) => [pc.photoCardId, pc]));

  const resultsWithPhotoCard = imageSearchResult.results.map((result) => ({
    ...result,
    candidates: result.candidates.map((candidate) => ({
      ...candidate,
      photoCard: photoCardMap.get(candidate.photoCardId)
    }))
  }));

  return {
    ...imageSearchResult,
    results: resultsWithPhotoCard
  };
};

const getImageSearchResult = async (
  url: string,
  body: Record<string, unknown>
): Promise<ImageSearchResponseType> => {
  try {
    const result = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken() || ''}`,
        'X-POCAPAN-API-KEY': `${apiKey()}`
      }
    });
    return await result.json();
  } catch (_error) {
    throw new Error('Error fetching ImageSearchResult');
  }
};

const getPhotoCardInfo = async (
  selectedMember: {
    artistId: string;
    memberId: string;
  },
  photoCardIds: string[]
): Promise<PhotoCardType[]> => {
  const encodedIds = photoCardIds.map((id) => encodeURIComponent(id)).join(',');
  const url = `/api/v1/photo_cards?artist_id=${selectedMember.artistId}&member_id=${selectedMember.memberId}&photo_card_ids=${encodedIds}&per_page=9999`;
  try {
    const photoCardInfo: { photoCards: PhotoCardType[]; meta: MetaType } = await queryGet(url);
    return photoCardInfo.photoCards;
  } catch (_error) {
    throw new Error('Error fetching PhotoCardInfo');
  }
};
